import { FC, useState, useEffect } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { trackEvent } from 'dibs-tracking';
import { graphql, useFragment } from 'react-relay';
import { getAbTestV2, trackAbTestV2Variant } from 'dibs-buyer-layout/exports/clientABTestV2';
import { isPriceRangeOrHigher } from '../PdpSharedPricingGuidance/pricingGuidanceHelpers';

import { Tooltip } from 'dibs-elements/exports/Tooltip';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton';

import classnames from 'classnames';
import dibsCss from 'dibs-css';

import { PdpRespMakeOfferNudge_item$key } from './__generated__/PdpRespMakeOfferNudge_item.graphql';

const TWENTY_SECONDS = 20 * 1000;
const MIN_OFFER_PERCENT_VALUE = 5;
const MAX_OFFER_PERCENT_VALUE = 20;
const TEST_NAME = 'PdpMakeOfferNudge';

const itemFragment = graphql`
    fragment PdpRespMakeOfferNudge_item on Item {
        ...pricingGuidanceHelpers_item
        pricing {
            netPrice {
                amount
            }
            retailPrice {
                amount
            }
        }
    }
`;

let trackingFired = false;

function trackAbTest(): void {
    if (!trackingFired) {
        trackAbTestV2Variant(TEST_NAME);
        trackingFired = true;
    }
}

export const getPercentOff = ({
    reducedAmount,
    originalAmount,
}: {
    reducedAmount: number;
    originalAmount: number;
}): number => {
    const percent = (reducedAmount / originalAmount) * 100;
    const percentOff = 100 - percent;
    return percentOff > 0 ? Math.round(Number(percentOff)) : 0;
};

export const PdpRespMakeOfferNudge: FC<{ item: PdpRespMakeOfferNudge_item$key }> = ({
    item: itemRef,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const shouldBeDisplayed = isPriceRangeOrHigher({ item, targetPriceRange: 'NEAR_MEDIAN_HIGH' });
    const isVariant = getAbTestV2(TEST_NAME)?.variant === 'variant';

    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (shouldBeDisplayed) {
            trackAbTest();
        }
    }, [shouldBeDisplayed]);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout | undefined;

        if (isVariant && shouldBeDisplayed) {
            timeoutId = setTimeout(() => {
                setIsOpen(true);
            }, TWENTY_SECONDS);
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [isVariant, shouldBeDisplayed]);

    useEffect(() => {
        if (isOpen) {
            trackEvent({
                category: 'product',
                action: 'make offer nudge view',
                label: 'pdp',
            });
        }
    }, [isOpen]);

    if (!shouldBeDisplayed || !isVariant) {
        return null;
    }

    let minPercent;
    let maxPercent;
    let netPercentage;

    const { pricing } = item;
    const netPriceAmount = pricing?.netPrice?.amount;
    const retailPriceAmount = pricing?.retailPrice?.amount;

    if (netPriceAmount && retailPriceAmount) {
        netPercentage = getPercentOff({
            reducedAmount: netPriceAmount,
            originalAmount: retailPriceAmount,
        });
    }

    if (netPercentage) {
        minPercent = netPercentage - MIN_OFFER_PERCENT_VALUE;
        maxPercent = netPercentage + MIN_OFFER_PERCENT_VALUE;
    } else {
        minPercent = MIN_OFFER_PERCENT_VALUE;
        maxPercent = MAX_OFFER_PERCENT_VALUE;
    }

    if (minPercent < MIN_OFFER_PERCENT_VALUE) {
        minPercent = MIN_OFFER_PERCENT_VALUE;
    }

    return (
        <div className={classnames(dibsCss.relative, dibsCss.topMedium)}>
            <Tooltip closeOnOutsideClick isVisible={isOpen} direction="left">
                <ModalCloseButton
                    onClick={() => {
                        trackEvent({
                            category: 'promo interaction',
                            action: 'make offer exit nudge',
                            label: 'pdp',
                            isInteractiveEvent: true,
                        });
                        setIsOpen(false);
                    }}
                />
                <div className={classnames(dibsCss.hyphensNone, dibsCss.mrXsmall)}>
                    <FormattedMessage
                        id="abf.PdpRespMakeOfferNudge.text.part1"
                        defaultMessage="<boldText>Negotiate.</boldText> We recommend <wrapper>offering {minPercent}-{maxPercent}% off the price.</wrapper>"
                        values={{
                            boldText: text => (
                                <span className={dibsCss.sassyFontBodyTypeHeavySizeSmall}>
                                    {text}
                                </span>
                            ),
                            wrapper: text => <span>{text}</span>,
                            minPercent,
                            maxPercent,
                        }}
                    />
                </div>
            </Tooltip>
        </div>
    );
};
